import type { RaidHudSnapshot } from "../../../features/raid";
import { t } from "../../../data/strings";
import { raidCatalog } from "../../../data/raid-catalog";
import hudPresetTable from "../../../data/tables/ui/huds/raid.json";
import { formatInt } from "../../../view/display-format";
import {
  createHpDamageFlashCtrl,
  resetHpDamageFlash,
  stepHpDamageFlash,
  type HpDamageFlashCtrl,
} from "../../../view/hp-damage-flash";
import { getUiLayout } from "../../layout";
import type { HudShellHandle } from "../../layout/hud-shell";
import type { UiHudPreset } from "../../layout/types";
import { resolveRaidCmdChip } from "./cmd-chip";
import { dockHtml, resultHtml, topHtml } from "./markup";

export type { RaidCmdChip } from "./cmd-chip";
export { resolveRaidCmdChip } from "./cmd-chip";

export type RaidHudHandlers = {
  onQuit: () => void;
  onRetry: () => void;
};

const preset = hudPresetTable as UiHudPreset;
const ELITE_LANES = 3;

function barColor(index: number): string {
  const pal = raidCatalog.view.colors.barPalette;
  return pal[index % pal.length] ?? "#ff3b5c";
}

function prepUnitNameKey(snap: RaidHudSnapshot): string | null {
  if (snap.prepUnitId < 0) return null;
  const u = snap.party.find((p) => p.id === snap.prepUnitId);
  if (!u) return null;
  const row = raidCatalog.roster.pool.find((p) => p.id === u.defId) as
    | { id: string; nameKey: string }
    | undefined;
  return row?.nameKey ?? null;
}

export function mountRaidHud(handlers: RaidHudHandlers): {
  sync: (snap: RaidHudSnapshot) => void;
  destroy: () => void;
} {
  const ui = getUiLayout();
  ui.sync();
  const shell: HudShellHandle = ui.mountShell(preset);

  const top = shell.region("top");
  top.style.pointerEvents = "none";
  top.innerHTML = topHtml();

  const mid = shell.region("mid");
  mid.style.pointerEvents = "none";

  const bottom = shell.region("bottom");
  bottom.style.pointerEvents = "none";
  bottom.innerHTML = dockHtml();

  const modal = shell.region("modal");
  modal.hidden = true;
  modal.replaceChildren();

  top.querySelector("[data-quit]")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onQuit();
  });

  const bindModal = () => {
    modal.querySelector("[data-retry]")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onRetry();
    });
    modal.querySelector("[data-quit]")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onQuit();
    });
  };

  let lastPhase: string | null = null;
  const laneFlash: HpDamageFlashCtrl[] = [];
  const laneUnitId: number[] = [];
  /** Last fill palette index per lane — detects multibar segment handoff. */
  const laneBarColorIndex: number[] = [];
  for (let i = 0; i < ELITE_LANES; i++) {
    laneFlash.push(createHpDamageFlashCtrl(1));
    laneUnitId.push(Number.NaN);
    laneBarColorIndex.push(Number.NaN);
  }

  const playBarBreakFx = (
    multibar: HTMLElement | null,
    barsEl: Element | null,
  ): void => {
    const bump = (el: HTMLElement | null) => {
      if (!el) return;
      el.classList.remove("is-bar-break");
      // Restart CSS animation when the same segment breaks again.
      void el.offsetWidth;
      el.classList.add("is-bar-break");
      const onEnd = () => {
        el.classList.remove("is-bar-break");
        el.removeEventListener("animationend", onEnd);
      };
      el.addEventListener("animationend", onEnd);
    };
    bump(multibar);
    bump(barsEl instanceof HTMLElement ? barsEl : null);
  };

  return {
    sync(snap: RaidHudSnapshot) {
      ui.sync();
      const assaultChips = top.querySelector(
        "[data-assault-chips]",
      ) as HTMLElement | null;
      const bossStack = top.querySelector(
        "[data-boss-stack]",
      ) as HTMLElement | null;
      const showAssault =
        snap.encounterMode === "assault" || snap.encounterMode === "transition";
      if (assaultChips) {
        assaultChips.hidden = !showAssault;
        const waveEl = assaultChips.querySelector("[data-assault-wave]");
        if (waveEl) {
          waveEl.textContent = t("raid.hud.wave", {
            n: String(snap.encounterWave),
            total: String(snap.encounterWaveTotal),
          });
        }
        const escBadge = assaultChips.querySelector(
          "[data-assault-escalate]",
        ) as HTMLElement | null;
        const escN = assaultChips.querySelector("[data-assault-escalate-n]");
        const stacks = snap.escalateStacks;
        if (escBadge) {
          const on = showAssault && stacks > 0;
          escBadge.hidden = !on;
          escBadge.setAttribute("aria-hidden", on ? "false" : "true");
          if (on) {
            const n = formatInt(stacks);
            const label = t("raid.hud.escalate", { n });
            escBadge.title = label;
            escBadge.setAttribute("aria-label", label);
            if (escN) escN.textContent = n;
          }
        }
      }
      if (bossStack) {
        bossStack.hidden = !snap.bossHudVisible;
      }

      const lanesRoot = top.querySelector(
        "[data-elite-lanes]",
      ) as HTMLElement | null;
      const bars = snap.bossHudVisible ? snap.eliteBars : [];
      if (lanesRoot) {
        lanesRoot.setAttribute("data-count", String(Math.max(1, bars.length)));
      }
      for (let i = 0; i < ELITE_LANES; i++) {
        const lane = top.querySelector(
          `[data-elite-lane="${i}"]`,
        ) as HTMLElement | null;
        if (!lane) continue;
        const row = bars[i];
        if (!row) {
          lane.hidden = true;
          continue;
        }
        lane.hidden = false;
        const nameEl = lane.querySelector("[data-elite-name]");
        if (nameEl) {
          nameEl.textContent =
            row.nameN != null
              ? t(row.nameKey, { n: String(row.nameN) })
              : t(row.nameKey);
        }
        const barsEl = lane.querySelector("[data-elite-bars]");
        if (barsEl) barsEl.textContent = row.barsLabel;
        const phaseEl = lane.querySelector("[data-elite-phase]");
        if (phaseEl) {
          phaseEl.textContent = row.phaseLabel ? t(row.phaseLabel) : "";
        }

        const fill = lane.querySelector(
          "[data-elite-fill]",
        ) as HTMLElement | null;
        const ghost = lane.querySelector(
          "[data-elite-ghost]",
        ) as HTMLElement | null;
        const multibar = lane.querySelector(
          "[data-elite-multibar]",
        ) as HTMLElement | null;
        const flash = laneFlash[i]!;
        let segmentBreak = false;
        if (laneUnitId[i] !== row.unitId) {
          resetHpDamageFlash(flash, row.hpFrac);
          laneUnitId[i] = row.unitId;
          laneBarColorIndex[i] = row.barColorIndex;
        } else if (laneBarColorIndex[i] !== row.barColorIndex) {
          // Colossus xN handoff: new segment — snap fill color/width, no morph.
          resetHpDamageFlash(flash, row.hpFrac);
          laneBarColorIndex[i] = row.barColorIndex;
          segmentBreak = true;
        }
        if (fill) {
          const step = stepHpDamageFlash(
            flash,
            row.hpFrac,
            performance.now(),
          );
          if (segmentBreak) {
            // Kill CSS transform tween so refill isn't a same-color width morph
            // over the next-segment track preview.
            fill.style.transition = "none";
            fill.style.transform = `scaleX(${step.live})`;
            fill.style.background = barColor(row.barColorIndex);
            void fill.offsetWidth;
            fill.style.transition = "";
            if (ghost) {
              ghost.style.transform = `scaleX(${step.live})`;
              ghost.style.opacity = "0";
            }
            playBarBreakFx(multibar, barsEl);
          } else {
            fill.style.transform = `scaleX(${step.live})`;
            fill.style.background = barColor(row.barColorIndex);
            if (ghost) {
              ghost.style.transform = `scaleX(${step.ghost})`;
              ghost.style.opacity = step.showGhost ? "1" : "0";
            }
          }
        }
        // Multi-segment boss: empty track previews the upcoming bar color.
        // Final bar (or single-bar elites) keeps the dark CSS track.
        // Track-only — never lerp fill color across segments.
        if (multibar) {
          const barsLeft = snap.focusBrute
            ? 1
            : Math.max(0, snap.bossBarsTotal - snap.bossBarIndex);
          multibar.style.background =
            barsLeft > 1 ? barColor(row.barColorIndex + 1) : "";
        }
        const toughWrap = lane.querySelector("[data-elite-toughbar]");
        const toughFill = lane.querySelector(
          "[data-elite-tough-fill]",
        ) as HTMLElement | null;
        if (toughFill) {
          toughFill.style.transform = `scaleX(${Math.max(0, Math.min(1, row.toughnessFrac))})`;
        }
        if (toughWrap) {
          toughWrap.classList.toggle("is-broken", row.broken);
        }
        const statusEl = lane.querySelector(
          "[data-elite-status]",
        ) as HTMLElement | null;
        if (statusEl) {
          const breakEl = statusEl.querySelector(
            "[data-elite-break]",
          ) as HTMLElement | null;
          const escEl = statusEl.querySelector(
            "[data-elite-escalate]",
          ) as HTMLElement | null;
          const escN = statusEl.querySelector("[data-elite-escalate-n]");
          const stacks = row.escalateStacks;
          const showBreak = row.broken;
          const showEsc = stacks > 0;
          if (breakEl) {
            breakEl.hidden = !showBreak;
            breakEl.setAttribute("aria-hidden", showBreak ? "false" : "true");
            if (showBreak) {
              const stunLabel = t("raid.hud.breakStun");
              breakEl.title = stunLabel;
              breakEl.setAttribute("aria-label", stunLabel);
            }
          }
          if (escEl) {
            escEl.hidden = !showEsc;
            escEl.setAttribute("aria-hidden", showEsc ? "false" : "true");
            if (showEsc) {
              const n = formatInt(stacks);
              const label = t("raid.hud.escalate", { n });
              escEl.title = label;
              escEl.setAttribute("aria-label", label);
              if (escN) escN.textContent = n;
            }
          }
          const showStatus = showBreak || showEsc;
          statusEl.hidden = !showStatus;
          statusEl.setAttribute("aria-hidden", showStatus ? "false" : "true");
        }
      }

      const party = top.querySelector("[data-party]");
      if (party) {
        party.textContent = t("raid.hud.party", {
          alive: String(snap.allyAlive),
          total: String(snap.allyTotal),
        });
      }
      const toast = top.querySelector("[data-toast]");
      if (toast) {
        toast.textContent = snap.toastKey ? t(snap.toastKey) : "";
      }

      const manaFill = bottom.querySelector(
        "[data-mana-fill]",
      ) as HTMLElement | null;
      if (manaFill) {
        const frac =
          snap.manaMax > 0 ? Math.max(0, Math.min(1, snap.mana / snap.manaMax)) : 0;
        manaFill.style.transform = `scaleX(${frac})`;
      }
      const manaText = bottom.querySelector("[data-mana-text]");
      if (manaText) {
        manaText.textContent = t("raid.fmt.mana", {
          n: formatInt(snap.mana),
          max: formatInt(snap.manaMax),
        });
      }
      const prepChip = bottom.querySelector(
        "[data-prep-chip]",
      ) as HTMLElement | null;
      const prepLab = bottom.querySelector("[data-prep-label]");
      const chip = resolveRaidCmdChip(snap);
      const chipOn = chip.mode !== "idle";
      if (prepChip) {
        prepChip.classList.toggle("is-active", chipOn);
        prepChip.classList.toggle("is-echo", chip.mode === "echo");
        prepChip.setAttribute("aria-hidden", chipOn ? "false" : "true");
      }
      if (prepLab) {
        if (chip.mode === "idle") {
          prepLab.textContent = "";
        } else if (chip.mode === "echo") {
          prepLab.textContent = t(chip.messageKey);
        } else {
          const nameKey = prepUnitNameKey(snap);
          const key = `raid.prep.${chip.kind}`;
          prepLab.textContent = nameKey
            ? t(key, { name: t(nameKey) })
            : t(key);
        }
      }

      const showResult = snap.phase === "won" || snap.phase === "lost";
      if (showResult) {
        if (snap.phase !== lastPhase) {
          modal.hidden = false;
          modal.innerHTML = resultHtml(snap.phase === "won");
          const title = modal.querySelector("[data-result-title]");
          if (title) {
            title.textContent =
              snap.phase === "won"
                ? t("raid.result.win")
                : t("raid.result.lose");
          }
          const body = modal.querySelector("[data-result-body]");
          if (body) {
            body.textContent =
              snap.phase === "won"
                ? t("raid.result.winBody")
                : t("raid.result.loseBody");
          }
          const retry = modal.querySelector("[data-retry]");
          if (retry) retry.textContent = t("raid.result.roster");
          const q = modal.querySelector("[data-quit]");
          if (q) q.textContent = t("hud.quit");
          bindModal();
        }
      } else if (!modal.hidden) {
        modal.hidden = true;
        modal.replaceChildren();
      }
      lastPhase = snap.phase;
    },
    destroy() {
      shell.destroy();
    },
  };
}
